import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { collection, getDocs, onSnapshot } from 'firebase/firestore';
import { db } from '../firebase';
import {
  MAX_CONTENT_WIDTH,
  SPACING_CARD,
  SPACING_CARD_BODY,
  SPACING_GROUP,
  SPACING_SECTION,
} from '../constants/layout';
import {
  CODA_EXTRABOLD_DISPLAY_SIZE,
  CODA_EXTRABOLD_MOBILE_SIZE,
} from '../constants/typography';

const heroURL =
  'https://s3-media0.fl.yelpcdn.com/bphoto/HXfYzuje-qrnyCd4lebE6A/o.jpg';

const parseDate = value =>
  !value ? null : value.toDate ? value.toDate() : new Date(value);

// FadeInImage stays using a 1:1 aspect ratio
function FadeInImage({ src, alt, onClick }) {
  const [loaded, setLoaded] = useState(false);

  return (
    <div className="image-container">
      <img
        src={src}
        alt={alt}
        onLoad={() => setLoaded(true)}
        onClick={onClick}
        className={`grid-img ${loaded ? 'loaded' : ''}`}
      />
    </div>
  );
}

export default function GalleryPage() {
    
  const [categories, setCategories] = useState([]);
  const [products, setProducts] = useState([]);
  const [images, setImages] = useState([]);
  const [sectionsLoaded, setSectionsLoaded] = useState(false);

  // This map will be: productID → [ “Roses”, “Mixed Bouquets”, … ]
  const [productToCatTitles, setProductToCatTitles] = useState({});

  const nav = useNavigate();

  // 1) Fetch categories & products once
  useEffect(() => {
    (async () => {
      const catSnap = await getDocs(collection(db, 'categories'));
      const fetchedCats = catSnap.docs.map(d => ({ id: d.id, ...d.data() }));
      setCategories(fetchedCats);

      const prodSnap = await getDocs(collection(db, 'products'));
      setProducts(prodSnap.docs.map(d => ({ id: d.id, ...d.data() })));

      // Build productID → [category titles] lookup
      const lookup = {};
      fetchedCats.forEach(cat => {
        // cat.itemIDs is assumed to be an array of product IDs
        ;(cat.itemIDs || []).forEach(pid => {
          if (!lookup[pid]) lookup[pid] = [];
          lookup[pid].push(cat.title);
        });
      });
      setProductToCatTitles(lookup);
    })();
  }, []);

  // 2) Real-time listener for all imageItems
  useEffect(() => {
    const unsub = onSnapshot(collection(db, 'imageItems'), snap => {
      const fetched = snap.docs.map(d => {
        const x = d.data();
        return {
          id: d.id,
          caption: x.caption ?? '',
          imageURL: x.imageURL ?? '',
          productID: x.productID ?? null,
          dateAdded: parseDate(x.dateAdded),
        };
      });
      setImages(fetched);

      if (fetched.length > 0) {
        setSectionsLoaded(true);
      }
    });
    return () => unsub();
  }, []);

  // Group images by category (for the “byCat” sections)
  const byCat = categories.map(cat => ({
    ...cat,
    images: images.filter(
      i =>
        i.productID &&
        Array.isArray(cat.itemIDs) &&
        cat.itemIDs.includes(i.productID)
    ),
  }));

  // Any images that didn’t match a category’s itemIDs
  const ungrouped = images.filter(
    i =>
      !i.productID ||
      !categories.some(
        cat =>
          Array.isArray(cat.itemIDs) && cat.itemIDs.includes(i.productID)
      )
  );

  const openProduct = pid => {
    const prod = products.find(p => p.id === pid);
    if (prod) nav(`/product/${prod.id}`);
  };

  return (
    <div className="gallery-container">
          {byCat.map(cat =>
            cat.images.length > 0 ? (
              <section
                key={cat.id}
                className={`category ${sectionsLoaded ? 'visible' : ''}`}
              >
                <div className="cat-head">
                  <h2 className="cat-title">
                    <Link
                      to={`/category/${cat.id}`}
                      className="cat-title-link"
                    >
                      {cat.title}
                    </Link>
                  </h2>
                  {cat.subtitle && <p className="cat-sub">{cat.subtitle}</p>}
                </div>
                <div className="grid">
                  {cat.images.map(img => {
                    const prod = products.find(p => p.id === img.productID);
                    // Grab the array of category titles for this product:
                    const catTitles = productToCatTitles[img.productID] || [];

                    return (
                      <button
                        key={img.id}
                        className="grid-btn"
                        onClick={() => openProduct(img.productID)}
                      >
                        <FadeInImage
                          src={img.imageURL}
                          alt={img.caption}
                          onClick={() => openProduct(img.productID)}
                        />

                        {/* 4) If caption is empty/null → show categories above product name */}
                        {(!img.caption || img.caption.trim() === '') && catTitles.length > 0 && (
                          <div className="image-caption">
                            {catTitles.join(', ')}
                          </div>
                        )}

                        {/* 5) Always show product name in its own div */}
                        {prod && (
                          <div className="product-name">
                            {prod.productName}
                          </div>
                        )}

                        {/* 6) If caption is present → show it below product name */}
                        {img.caption && img.caption.trim() !== '' && (
                          <div className="image-caption">
                            {img.caption}
                          </div>
                        )}
                      </button>
                    );
                  })}
                </div>
              </section>
            ) : null
          )}

          {ungrouped.length > 0 && (
            <section
              className={`category ${sectionsLoaded ? 'visible' : ''}`}
            >
              <div className="cat-head">
                <h2 className="cat-title">More Photos</h2>
              </div>
              <div className="grid">
                {ungrouped.map(img => {
                  const prod = products.find(p => p.id === img.productID);
                  const catTitles = productToCatTitles[img.productID] || [];

                  return (
                    <div key={img.id} className="grid-btn">
                      <FadeInImage
                        src={img.imageURL}
                        alt={img.caption}
                        onClick={() =>
                          prod && openProduct(img.productID)
                        }
                      />

                      {(!img.caption || img.caption.trim() === '') && catTitles.length > 0 && (
                        <div className="image-caption">
                          {catTitles.join(', ')}
                        </div>
                      )}

                      {prod && (
                        <div
                          className="product-name"
                          onClick={() => openProduct(img.productID)}
                        >
                          {prod.productName}
                        </div>
                      )}

                      {img.caption && img.caption.trim() !== '' && (
                        <div className="image-caption">
                          {img.caption}
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            </section>
          )}

      <style jsx>{`
        .gallery-container {
          max-width: ${MAX_CONTENT_WIDTH};
          margin: 0 auto;
          padding: ${SPACING_SECTION} ${SPACING_CARD};
          padding-left: max(${SPACING_CARD}, env(safe-area-inset-left));
          padding-right: max(${SPACING_CARD}, env(safe-area-inset-right));
        }

        .category {
          margin-bottom: ${SPACING_SECTION};
          opacity: 0;
          transform: translateY(20px);
          transition: opacity 0.5s ease, transform 0.5s ease;
        }

        .category.visible {
          opacity: 1;
          transform: translateY(0);
        }

        .cat-head {
          margin-bottom: ${SPACING_CARD};
        }

        .cat-title {
          margin: 0;
          font-size: 1.25rem;
          font-weight: 600;
          font-family: var(--font-family-base);
          text-transform: uppercase;
          letter-spacing: 0.06em;
        }

        .cat-title-link {
          color: inherit;
          text-decoration: none;
          cursor: pointer;
        }

        .cat-title-link:hover,
        .cat-title-link:focus {
          text-decoration: underline;
        }

        .cat-sub {
          margin: 4px 0 0;
          font-size: 0.875rem;
          color: var(--color-text-muted);
        }

        .grid {
          display: grid;
          width: 100%;
          row-gap: ${SPACING_SECTION};
          column-gap: ${SPACING_GROUP};
          grid-template-columns: repeat(3, 1fr);
        }

        @media (min-width: 1400px) {
          .grid {
            grid-template-columns: repeat(4, 1fr);
            column-gap: ${SPACING_GROUP};
            row-gap: ${SPACING_SECTION};
          }
        }

        @media (min-width: 1000px) and (max-width: 1399px) {
          .grid {
            grid-template-columns: repeat(3, 1fr);
            column-gap: ${SPACING_GROUP};
            row-gap: ${SPACING_SECTION};
          }
        }

        @media (max-width: 999px) {
          .grid {
            grid-template-columns: repeat(2, 1fr);
            column-gap: ${SPACING_CARD_BODY};
            row-gap: ${SPACING_GROUP};
          }
        }

        @media (max-width: 600px) {
          .grid {
            grid-template-columns: repeat(2, 1fr);
            column-gap: ${SPACING_CARD};
            row-gap: ${SPACING_CARD_BODY};
          }
        }

        .grid-btn {
          display: flex;
          flex-direction: column;
          align-items: center;
          padding: 0;
          border: none;
          background: none;
          cursor: pointer;
          transition: transform 0.2s ease, box-shadow 0.2s ease;
        }

        .grid-btn:hover {
          transform: translateY(-2px);
        }

        .grid-btn:focus {
          outline: 2px solid var(--color-link);
          outline-offset: 2px;
        }

        .image-container {
          position: relative;
          width: 100%;
          box-sizing: border-box;
          overflow: hidden;
          border: 1px solid var(--color-border-strong);
          border-radius: 0;
        }

        .image-container::before {
          content: '';
          display: block;
          padding-top: 100%;
        }

        .grid-img {
          position: absolute;
          top: 0;
          left: 0;
          width: 100%;
          height: 100%;
          display: block;
          object-fit: cover;
          opacity: 0;
          transition: opacity 0.5s ease-in;
        }

        .grid-img.loaded {
          opacity: 1;
        }

        .product-name {
          margin-top: 0px;
          font-size: 0.875rem;
          color: var(--color-text-primary);
          text-align: center;
          word-break: break-word;
          font-weight: 600;
          line-height: 1.3;
        }

        .image-caption {
          margin-top: 2px;
          margin-bottom: 2px;
          font-size: 0.75rem;
          color: var(--color-text-secondary);
          text-align: center;
          word-break: break-word;
        }
      `}</style>
    </div>
  );
}
